import { useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../hooks';
import { selectIsAuthenticated } from '../containers/authSlice';
import { repositoryEntryUpdateLocalAction } from '../containers/contentSlice';
import { useRepositoryEntryUpdateMutation } from '../services/repositoryEntries';
import type { ControlledFormData } from '../components/EditEntryForm';
import type { UnsavedChangesDialogProps } from '../components/UnsavedChangesDialog';
import type { RelationValue } from '../components/EntryPropertyBelongsToEditDialog';
import { BelongsToPopulatedValue, PopulatedEntry, PropertyDefinitionBelongsTo, PropertyDefinitionHas } from '../types';

const NO_RELATION_VALUES: RelationValue[] = [];

type PendingChanges = {
	entryId: string;
	/** Update payload; relation slugs hold target entry ids. */
	values: ControlledFormData;
	/** Labeled relation values for display, keyed by slug. */
	relationValues: Record<string, RelationValue[]>;
};

export type UseRepositoryEntryDrawerChangesResult = ReturnType<typeof useRepositoryEntryDrawerChanges>;

/**
 * Unsaved field edits of the repository entry drawer. Lives in the view hosting the drawer, because
 * that view owns every way of closing or switching the drawer — route those through `requestEntryChange`.
 */
export function useRepositoryEntryDrawerChanges() {
	const dispatch = useDispatch();
	const isAuthenticated = useAppSelector(selectIsAuthenticated);
	const [repositoryEntryUpdateMutation] = useRepositoryEntryUpdateMutation();
	const [pendingChanges, setPendingChangesState] = useState<PendingChanges | null>(null);
	const pendingChangesRef = useRef(pendingChanges);
	const [leaveAction, setLeaveAction] = useState<(() => void) | null>(null);

	// The ref is updated synchronously so a leave action run right after save/discard sees no pending changes.
	const setPendingChanges = useCallback((next: PendingChanges | null) => {
		pendingChangesRef.current = next;
		setPendingChangesState(next);
	}, []);

	const publishEntryValues = useCallback((entryId: string, values: ControlledFormData, optimisticValues: Record<string, RelationValue[]>) => {
		if (isAuthenticated) {
			repositoryEntryUpdateMutation({ id: entryId, values, optimisticValues });
		} else {
			dispatch(repositoryEntryUpdateLocalAction({ id: entryId, values }));
		}
	}, [isAuthenticated, repositoryEntryUpdateMutation, dispatch]);

	const addPendingValues = useCallback((entryId: string, values: ControlledFormData) => {
		const pending = pendingChangesRef.current;
		setPendingChanges({
			entryId,
			values: { ...pending?.values, ...values },
			relationValues: pending?.relationValues ?? {},
		});
	}, [setPendingChanges]);

	const setPendingRelationValues = useCallback((
		entryId: string,
		def: PropertyDefinitionBelongsTo | PropertyDefinitionHas,
		nextValues: RelationValue[],
	) => {
		const pending = pendingChangesRef.current;
		setPendingChanges({
			entryId,
			values: { ...pending?.values, [def.slug]: nextValues.map(({ value }) => value) },
			relationValues: { ...pending?.relationValues, [def.slug]: nextValues },
		});
	}, [setPendingChanges]);

	const getRelationValues = useCallback((entry: PopulatedEntry, def: PropertyDefinitionBelongsTo | PropertyDefinitionHas): RelationValue[] => {
		const pendingValues = pendingChanges?.entryId === entry.id ? pendingChanges.relationValues[def.slug] : undefined;
		return pendingValues ?? (entry.data[def.slug] as BelongsToPopulatedValue[] | undefined) ?? NO_RELATION_VALUES;
	}, [pendingChanges]);

	const savePendingChanges = useCallback(() => {
		const pending = pendingChangesRef.current;
		if (!pending) {
			return;
		}
		publishEntryValues(pending.entryId, pending.values, pending.relationValues);
		setPendingChanges(null);
	}, [publishEntryValues, setPendingChanges]);

	const discardPendingChanges = useCallback(() => setPendingChanges(null), [setPendingChanges]);

	const requestEntryChange = useCallback((nextEntryId: string | null, action: () => void) => {
		const pending = pendingChangesRef.current;
		if (!pending || pending.entryId === nextEntryId) {
			action();
			return;
		}
		setLeaveAction(() => action);
	}, []);

	const unsavedChangesDialogProps = useMemo((): UnsavedChangesDialogProps => ({
		open: Boolean(leaveAction),
		onContinueEditing: () => setLeaveAction(null),
		onDiscard: () => {
			discardPendingChanges();
			leaveAction?.();
			setLeaveAction(null);
		},
		onSave: () => {
			savePendingChanges();
			leaveAction?.();
			setLeaveAction(null);
		},
	}), [leaveAction, discardPendingChanges, savePendingChanges]);

	return useMemo(() => ({
		pendingChanges,
		isUnsavedChangesDialogOpen: Boolean(leaveAction),
		addPendingValues,
		setPendingRelationValues,
		getRelationValues,
		savePendingChanges,
		discardPendingChanges,
		requestEntryChange,
		unsavedChangesDialogProps,
	}), [
		pendingChanges,
		leaveAction,
		addPendingValues,
		setPendingRelationValues,
		getRelationValues,
		savePendingChanges,
		discardPendingChanges,
		requestEntryChange,
		unsavedChangesDialogProps,
	]);
}
